/**
 * @prettier
 * @author Nic Ebner
 * @author Alex Clary
 */
import { genericPostingActionCreator, genericFetchingActionCreator } from 'app/actions/genericActionCreator'
import { catchCommonErrors } from 'app/actions/commonActionUtils'
import { createChunkRequest, getChunksFromFile, convertChunkToBase64 } from 'app/utils/chunkedUploadUtils'

export const uploadFile = (file, chunkSize) => async dispatch => {
  if (!file) {
    return null
  }
  console.log(`Uploading file ${file?.name} with chunkSize ${chunkSize}`)
  dispatch(genericPostingActionCreator('INIT_CHUNKED_UPLOAD', 'posting'))
  try {
    const response = await fetch('/lgyhub/file-manager/api/files/chunked', {
      credentials: 'include',
      method: 'POST',
      body: createChunkRequest(file, chunkSize),
      headers: new Headers({ 'Content-Type': 'application/json' })
    })
    const responseJson = await catchCommonErrors(dispatch, response, `uploading ${file?.name}`)

    if (responseJson.status >= 400) {
      throw new Error(responseJson.errors[0].message)
    } else {
      dispatch(genericPostingActionCreator('INIT_CHUNKED_UPLOAD', 'posted', responseJson))
      dispatch({ type: 'CHUNK_RESET' })
      const fileId = responseJson.fileId
      const fileChunks = getChunksFromFile(file, chunkSize)
      fileChunks.forEach((chunk, index) => dispatch(uploadChunks(file?.name, fileId, index, chunk, fileChunks.length)))
    }
  } catch (e) {
    console.error(e)
    dispatch(genericPostingActionCreator('INIT_CHUNKED_UPLOAD', 'error', { error: e }))
  }
}

export const uploadChunks = (filename, fileId, chunkIndex, chunk, totalFileChunks, retries = 5) => async (
  dispatch,
  getState
) => {
  dispatch(genericPostingActionCreator('CHUNK_UPLOAD', 'posting'))
  const base64Chunk = await convertChunkToBase64(chunk)
  try {
    const response = await fetch(`/lgyhub/file-manager/api/files/${fileId}/chunks/${chunkIndex}`, {
      credentials: 'include',
      method: 'PUT',
      body: JSON.stringify({ chunkContentsBase64: base64Chunk }),
      headers: new Headers({ 'Content-Type': 'application/json' })
    })
    const responseJson = await catchCommonErrors(dispatch, response, `uploading ${filename}`)
    const completedChunks = getState().chunkedUploads.uploadChunk.completedChunks
    if (responseJson.status >= 400) {
      if (retries > 0) {
        dispatch(uploadChunks(filename, fileId, chunkIndex, chunk, totalFileChunks, --retries))
      } else {
        throw new Error(responseJson.errors[0].message)
      }
    } else if (totalFileChunks - 1 === completedChunks) {
      dispatch(checkUploadComplete(fileId, filename))
    } else {
      dispatch({ type: 'CHUNK_COMPLETED' })
    }
  } catch (e) {
    if (retries > 0) {
      dispatch(uploadChunks(filename, fileId, chunkIndex, chunk, totalFileChunks, --retries))
    } else {
      console.error(e)
      dispatch(genericPostingActionCreator('CHUNK_UPLOAD', 'error', { error: e }))
    }
  }
}

const checkUploadComplete = (fileId, filename) => async dispatch => {
  try {
    const response = await fetch(`/lgyhub/file-manager/api/files/${fileId}/chunks`, {
      credentials: 'include',
      method: 'GET'
    })
    const responseJson = await catchCommonErrors(dispatch, response, `uploading ${filename}`)

    if (!responseJson.chunkIds.includes(null)) {
      dispatch(genericPostingActionCreator('CHUNK_UPLOAD', 'posted', { uploadComplete: true }))
    } else {
      throw new Error('Some chunks failed to upload.')
    }
  } catch (e) {
    console.error(e)
    dispatch(genericPostingActionCreator('CHUNK_UPLOAD', 'error', { error: e }))
  }
}

export const fetchChunkedUploadProperties = () => async dispatch => {
  console.log('Fetching file-manager properties')
  try {
    const response = await fetch(`/lgyhub/file-manager/api/files/properties`, {
      credentials: 'include',
      method: 'GET'
    })
    const responseJson = await catchCommonErrors(dispatch, response, `fetching file upload properties`)

    dispatch(genericFetchingActionCreator('CHUNK_UPLOAD_PROPERTIES', 'fetched', responseJson))
  } catch (e) {
    console.error(e)
    dispatch(genericFetchingActionCreator('CHUNK_UPLOAD_PROPERTIES', 'error', { error: e }))
  }
}
